import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { aiService } from '../../services/api';
import {
  FiZap, FiX, FiSend, FiLoader, FiTrash2, FiMessageSquare, FiPlus,
  FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch, FiEdit, FiTrash,
  FiCheck, FiAlertCircle, FiPaperclip, FiFile, FiMaximize2, FiMinimize2,
  FiDownload, FiFileText, FiCopy, FiTrendingUp, FiActivity, FiChevronDown,
  FiChevronUp, FiLayers, FiHelpCircle, FiClock
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { marked } from 'marked';
import AIChartCard from './AIChartCard';

function getToolIcon(fn) {
  if (!fn) return FiSearch;
  if (fn.includes('export') || fn.includes('download')) return FiDownload;
  if (fn.includes('_list') || fn.includes('_index') || fn.includes('_get') || fn.includes('_show')) return FiSearch;
  if (fn.includes('_create') || fn.includes('_store') || fn.includes('_update') || fn.includes('_edit')) return FiEdit;
  if (fn.includes('_delete') || fn.includes('_destroy') || fn.includes('_remove')) return FiTrash;
  return FiZap;
}

function getToolColor(fn) {
  if (!fn) return '#3b82f6';
  if (fn.includes('export') || fn.includes('download')) return '#10b981';
  if (fn.includes('_list') || fn.includes('_index') || fn.includes('_get') || fn.includes('_show')) return '#3b82f6';
  if (fn.includes('_create') || fn.includes('_store')) return '#16a34a';
  if (fn.includes('_update') || fn.includes('_edit')) return '#f59e0b';
  if (fn.includes('_delete') || fn.includes('_destroy') || fn.includes('_remove')) return '#ef4444';
  return '#8b5cf6';
}

function getActionLabel(fn) {
  if (!fn) return 'Consulta';
  if (fn.includes('export')) return 'Exportação';
  if (fn.includes('_list') || fn.includes('_index')) return 'Listagem';
  if (fn.includes('_get') || fn.includes('_show')) return 'Detalhes';
  if (fn.includes('_create') || fn.includes('_store')) return 'Criação';
  if (fn.includes('_update') || fn.includes('_edit')) return 'Atualização';
  if (fn.includes('_delete') || fn.includes('_destroy') || fn.includes('_remove')) return 'Exclusão';
  return 'Ação';
}

function formatParamValue(val) {
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function getFullDownloadUrl(url, apiBase) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = apiBase ? apiBase.replace(/\/api\/?$/, '') : '';
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

function renderAiMarkdown(content, apiBase) {
  const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
  const renderer = new marked.Renderer();
  renderer.link = ({ href, title, text }) => {
    const fullHref = getFullDownloadUrl(href, apiBase);
    const titleAttr = title ? ` title="${title}"` : '';
    const isDownload = href.includes('/download') || href.endsWith('.xlsx') || href.endsWith('.csv');
    const dlAttr = isDownload ? ' download' : '';
    return `<a href="${fullHref}" target="_blank" rel="noopener noreferrer"${titleAttr}${dlAttr} style="color: #0284c7; text-decoration: underline; font-weight: 500;">${text}</a>`;
  };
  return marked.parse(text, { breaks: true, gfm: true, renderer });
}

function parseAiMessageSegments(content) {
  if (typeof content !== 'string') {
    return [{ type: 'markdown', content: String(content || '') }];
  }

  const rawText = content.trim();

  if (rawText.startsWith('{') && rawText.endsWith('}')) {
    try {
      const parsed = JSON.parse(rawText);
      if (parsed && (parsed.data || parsed.series) && (parsed.type || parsed.chartType)) {
        return [{ type: 'chart', config: parsed }];
      }
    } catch {
      // not direct JSON, continue
    }
  }

  const segments = [];
  const regex = /```(?:chart|json:chart|json)?\s*([\s\S]*?)```/gi;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const fullMatch = match[0];
    const blockContent = match[1].trim();
    const startIndex = match.index;

    let isChart = false;
    let chartConfig = null;

    if (fullMatch.toLowerCase().startsWith('```chart') || fullMatch.toLowerCase().startsWith('```json:chart')) {
      try {
        chartConfig = JSON.parse(blockContent);
        if (chartConfig && typeof chartConfig === 'object') {
          isChart = true;
        }
      } catch (e) {
        try {
          const cleaned = blockContent.replace(/,\s*([}\]])/g, '$1');
          chartConfig = JSON.parse(cleaned);
          if (chartConfig && typeof chartConfig === 'object') {
            isChart = true;
          }
        } catch {}
      }
    } else {
      try {
        const parsed = JSON.parse(blockContent);
        if (parsed && typeof parsed === 'object' && (parsed.data || parsed.series) &&
            (parsed.type || parsed.chartType || parsed.xAxisKey || parsed.nameKey)) {
          const type = (parsed.type || parsed.chartType || '').toLowerCase();
          if (['bar', 'line', 'area', 'pie', 'doughnut', 'donut', 'column'].includes(type) || Array.isArray(parsed.data)) {
            isChart = true;
            chartConfig = parsed;
          }
        }
      } catch {}
    }

    if (isChart && chartConfig) {
      if (startIndex > lastIndex) {
        const textBefore = content.substring(lastIndex, startIndex).trim();
        if (textBefore) {
          segments.push({ type: 'markdown', content: textBefore });
        }
      }
      segments.push({ type: 'chart', config: chartConfig });
      lastIndex = startIndex + fullMatch.length;
    }
  }

  if (lastIndex < content.length) {
    const remainingText = content.substring(lastIndex).trim();
    if (remainingText) {
      segments.push({ type: 'markdown', content: remainingText });
    }
  }

  if (segments.length === 0) {
    segments.push({ type: 'markdown', content: content });
  }

  return segments;
}

function renderExportDownloadCards(toolCalls, apiBase) {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return null;

  const exportCards = [];
  toolCalls.forEach((tc, idx) => {
    const rawRes = tc.result?.response ?? tc.result;
    const data = rawRes?.data ?? rawRes;
    const downloadUrl = data?.download_url || rawRes?.download_url;
    if (downloadUrl) {
      const filename = data?.filename || rawRes?.filename || 'relatorio_faturamento.xlsx';
      const fullUrl = getFullDownloadUrl(downloadUrl, apiBase);
      const fileSize = data?.file_size ? `${Math.round(data.file_size / 1024)} KB` : null;
      const reportsCount = data?.reports_count || (Array.isArray(data?.reports) ? data.reports.length : null);

      exportCards.push(
        <div
          key={`exp-card-${idx}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '1px solid #86efac',
            borderRadius: 10,
            padding: '10px 14px',
            marginTop: 10,
            marginBottom: 6,
            gap: 12,
            boxShadow: '0 2px 8px rgba(22, 163, 74, 0.08)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#16a34a',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
            }}>
              <FiFileText size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontWeight: 600,
                color: '#14532d',
                fontSize: '0.78rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} title={filename}>
                {filename}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#15803d', marginTop: 1 }}>
                Planilha Excel {fileSize ? `• ${fileSize}` : ''} {reportsCount ? `• ${reportsCount} relatório(s)` : ''}
              </div>
            </div>
          </div>
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={filename}
            style={{
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontWeight: 600,
              fontSize: '0.74rem',
              padding: '7px 14px',
              borderRadius: 8,
              background: '#16a34a',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <FiDownload size={13} /> Baixar
          </a>
        </div>
      );
    }
  });

  return exportCards.length > 0 ? <div>{exportCards}</div> : null;
}

function getResultSummary(result, fn) {
  if (!result || result.error) return null;
  const res = result?.response ?? result;
  if (!res || typeof res !== 'object') return null;
  const data = res.data || res;
  
  if (data.download_url) {
    return data.filename ? `Planilha: ${data.filename}` : 'Planilha gerada com sucesso';
  }

  const listKeys = ['goals', 'filters', 'variables', 'reports', 'modules', 'vendedores', 'funcionarios', 'departamentos', 'atividades', 'secoes', 'marcas', 'filiais', 'produtos', 'categorias', 'employees'];
  for (const key of listKeys) {
    if (Array.isArray(data[key])) {
      const count = data[key].length;
      const total = data.total !== undefined ? data.total : count;
      return `Encontrados ${total} registro(s)`;
    }
  }
  
  if (data.id || data.goal?.id || data.filter?.id || data.variable?.id) {
    const id = data.id || data.goal?.id || data.filter?.id || data.variable?.id;
    return `ID: ${id}`;
  }
  
  if (data.success || data.message) {
    return data.message || 'Operação concluída';
  }
  
  return null;
}

function ToolCallCard({ toolCall, index, apiBase }) {
  const fn = toolCall.function || '';
  const desc = toolCall.function_description || fn;
  const params = toolCall.parameters || {};
  const result = toolCall.result || {};
  const hasError = result?.error || result?.http_code >= 400;
  const color = hasError ? '#ef4444' : getToolColor(fn);
  const Icon = getToolIcon(fn);
  const summary = getResultSummary(result, fn);

  const rawRes = result?.response ?? result;
  const data = rawRes?.data ?? rawRes;
  const dlUrl = data?.download_url || rawRes?.download_url;
  const filename = data?.filename || rawRes?.filename || 'relatorio.xlsx';
  const fullDlUrl = dlUrl && apiBase ? getFullDownloadUrl(dlUrl, apiBase) : dlUrl;
  
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '7px 10px', borderRadius: 8,
      background: hasError ? '#fef2f2' : '#f8fafc',
      border: `1px solid ${hasError ? '#fecaca' : '#e2e8f0'}`,
      fontSize: '0.72rem', lineHeight: 1.35,
      marginBottom: 4,
      transition: 'all 0.15s ease'
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: color, color: 'white', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Icon size={12} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            background: hasError ? '#fee2e2' : '#eff6ff',
            color: hasError ? '#b91c1c' : '#1d4ed8',
            padding: '1px 6px',
            borderRadius: 4,
            fontSize: '0.64rem',
            fontWeight: 700
          }}>
            {getActionLabel(fn)}
          </span>
          <span style={{ fontWeight: 500, color: '#475569' }}>
            {desc.replace(/^[^:]+:\s*/, '')}
          </span>
        </div>
        {Object.keys(params).length > 0 && (
          <div style={{ color: '#64748b', fontSize: '0.67rem', margin: '3px 0', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(params).map(([k, v]) => (
              <span key={k} style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                padding: '1px 5px',
                borderRadius: 4,
                whiteSpace: 'nowrap'
              }}>
                <span style={{ color: '#94a3b8' }}>{k}:</span> {formatParamValue(v)}
              </span>
            ))}
          </div>
        )}
        {summary && (
          <div style={{ color: hasError ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            {hasError ? <FiAlertCircle size={11} /> : <FiCheck size={11} />}
            <span>{hasError ? result.error || 'Erro na operação' : summary}</span>
          </div>
        )}
        {dlUrl && !hasError && (
          <div style={{ marginTop: 5 }}>
            <a
              href={fullDlUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={filename}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 6,
                background: '#16a34a', color: '#ffffff',
                textDecoration: 'none', fontSize: '0.68rem', fontWeight: 600,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <FiDownload size={11} /> Baixar Planilha
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolCallsAccordion({ toolCalls, apiBase }) {
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return null;
  const count = toolCalls.length;
  const hasError = toolCalls.some(tc => tc.result?.error || tc.result?.http_code >= 400);

  return (
    <div style={{
      margin: '6px 0 10px',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#f8fafc'
    }}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.72rem',
          color: '#475569',
          fontWeight: 600,
          transition: 'background 0.15s ease'
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiActivity size={12} color={hasError ? '#ef4444' : '#3b82f6'} />
          <span>{count === 1 ? '1 ação executada' : `${count} ações executadas`}</span>
          {hasError && (
            <span style={{ fontSize: '0.62rem', background: '#fee2e2', color: '#ef4444', padding: '1px 5px', borderRadius: 4 }}>
              Atenção
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#94a3b8' }}>
          <span>{expanded ? 'Ocultar' : 'Ver detalhes'}</span>
          {expanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '4px 8px 8px', borderTop: '1px solid #f1f5f9', background: '#ffffff' }}>
          {toolCalls.map((tc, tci) => (
            <ToolCallCard key={tci} toolCall={tc} index={tci} apiBase={apiBase} />
          ))}
        </div>
      )}
    </div>
  );
}

function formatToolCallResult(result, functionName) {
  if (!result || typeof result !== 'object') return String(result ?? '');
  
  const responseData = result?.response ?? result;
  
  if (!responseData || typeof responseData !== 'object') {
    return typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2);
  }
  
  if (functionName === 'db_describe_table' && responseData?.columns) {
    const cols = responseData.columns;
    if (Array.isArray(cols) && cols.length > 0) {
      const lines = cols.map(c => {
        const name = c.Field || c.column_name || c.name || '';
        const type = c.Type || c.column_type || '';
        const parts = [`**${name}**`, type];
        if (c.Key === 'PRI') parts.push('PK');
        if (c.Key === 'MUL') parts.push('FK');
        if (c.Null === 'NO') parts.push('NOT NULL');
        return '- ' + parts.filter(Boolean).join(' | ');
      });
      if (responseData.relations && responseData.relations.length > 0) {
        lines.push('\n**Relacionamentos:**');
        responseData.relations.forEach(r => {
          lines.push(`- ${r.from_table}.${r.from_column} → ${r.to_table}.${r.to_column}`);
        });
      }
      return lines.join('\n');
    }
  }

  if (responseData?.data) {
    const innerData = responseData.data;
    if (Array.isArray(innerData) && innerData.length > 0) {
      return innerData.slice(0, 50).map(item => {
        if (typeof item === 'object') {
          const label = item.nome || item.name || item.titulo || item.codigo || item.id || JSON.stringify(item);
          return `- **${label}**`;
        }
        return `- ${item}`;
      }).join('\n');
    }
    if (typeof innerData === 'object' && !Array.isArray(innerData)) {
      const entries = Object.entries(innerData);
      if (entries.length <= 10) {
        return entries.map(([k, v]) => `- **${k}:** ${v}`).join('\n');
      }
    }
  }
  
  return typeof responseData === 'string' ? responseData : JSON.stringify(responseData, null, 2);
}

function isTechnicalDump(text) {
  return typeof text === 'string' && (text.startsWith('Função:') || text.startsWith('{"http_code"'));
}

function extractToolsUsed(data) {
  if (!Array.isArray(data?.tool_calls) || data.tool_calls.length === 0) return null;
  return data.tool_calls
    .map((tc) => tc.function_description || tc.function)
    .filter(Boolean);
}

function formatAiResponse(data) {
  if (!data || typeof data !== 'object') return String(data ?? '');

  if (data.response && typeof data.response === 'string' && !isTechnicalDump(data.response)) {
    return data.response;
  }

  if (data.message) return data.message;

  if (Array.isArray(data.tool_calls) && data.tool_calls.length > 0) {
    return data.tool_calls.map((tc) => {
      const label = tc.function_description || tc.function || 'Consulta';
      const result = tc.result;
      if (result?.error) return `**${label}**\n\nErro: ${result.error}`;
      const functionName = tc.function || '';
      return `**${label}**\n\n${formatToolCallResult(result, functionName)}`;
    }).join('\n\n');
  }

  if (data.result) {
    return typeof data.result === 'string' ? data.result : JSON.stringify(data.result, null, 2);
  }

  return 'Não foi possível processar a resposta.';
}

function CategoryScrollPills({ categories, activeCategory, onSelectCategory, categoryCounts = {} }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, categories]);

  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const distance = 140 * direction;
    scrollRef.current.scrollBy({ left: distance, behavior: 'smooth' });
  };

  const handleWheel = (e) => {
    if (e.deltaY !== 0 && scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  };

  return (
    <div className="ai-chat-categories-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => handleScroll(-1)}
          className="ai-chat-category-nav-btn left"
          title="Rolar categorias para esquerda"
          aria-label="Rolar para esquerda"
        >
          <FiChevronLeft size={13} />
        </button>
      )}

      <div
        ref={scrollRef}
        className="ai-chat-categories-scroll"
        onWheel={handleWheel}
        style={{
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          padding: '4px 2px 8px 2px',
          width: '100%',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {categories.map((cat) => {
          const isActive = activeCategory === cat;
          const count = categoryCounts[cat];
          return (
            <button
              key={cat}
              type="button"
              onClick={() => onSelectCategory(cat)}
              className={`ai-chat-category-pill ${isActive ? 'active' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 10px',
                fontSize: '0.72rem',
                borderRadius: '16px',
                border: isActive ? '1px solid var(--primary)' : '1px solid var(--border)',
                background: isActive ? 'var(--primary)' : 'var(--white)',
                color: isActive ? '#ffffff' : 'var(--text-main)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: isActive ? 600 : 500,
                transition: 'all 0.15s ease',
                flexShrink: 0,
                boxShadow: isActive ? '0 2px 6px rgba(26, 26, 26, 0.2)' : 'none',
              }}
            >
              <span>{cat}</span>
              {count !== undefined && (
                <span
                  style={{
                    fontSize: '0.62rem',
                    padding: '1px 5px',
                    borderRadius: '10px',
                    background: isActive ? 'rgba(255, 255, 255, 0.25)' : '#f1f5f9',
                    color: isActive ? '#ffffff' : '#64748b',
                    fontWeight: 700,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {canScrollRight && (
        <button
          type="button"
          onClick={() => handleScroll(1)}
          className="ai-chat-category-nav-btn right"
          title="Rolar categorias para direita"
          aria-label="Rolar para direita"
        >
          <FiChevronRight size={13} />
        </button>
      )}
    </div>
  );
}

const QUICK_SUGGESTIONS = [
  {
    icon: FiTrendingUp,
    title: 'Faturamento do Mês',
    desc: 'Como está o faturamento comparado às metas?',
    prompt: 'Como está o faturamento geral deste mês em relação às metas estabelecidas?'
  },
  {
    icon: FiActivity,
    title: 'Melhores Vendedores',
    desc: 'Ranking dos 5 melhores vendedores e atingimento',
    prompt: 'Apresente o ranking dos 5 melhores vendedores deste mês com faturamento e percentual de atingimento.'
  },
  {
    icon: FiFileText,
    title: 'Relatórios Pendentes',
    desc: 'Status dos relatórios de vendas e fechamentos',
    prompt: 'Liste os relatórios de faturamento mais recentes e informe se há pendências de fechamento.'
  },
  {
    icon: FiSearch,
    title: 'Metas e Variáveis',
    desc: 'Metas com menor percentual de atingimento',
    prompt: 'Quais metas de vendas estão com menor percentual de atingimento neste período?'
  }
];

function AIChatDrawer({ open, onToggle, panelWidth = 420, onWidthChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sidebarDragging, setSidebarDragging] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(210);
  const [conversations, setConversations] = useState([]);
  const [conversationSearch, setConversationSearch] = useState('');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [copiedMsgIndex, setCopiedMsgIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const lastUserMsgRef = useRef('');
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('Todos');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [interimStatus, setInterimStatus] = useState(null);
  const [interimToolCalls, setInterimToolCalls] = useState([]);
  const [interimSteps, setInterimSteps] = useState([]);
  const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'http://localhost:8000/api';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, interimSteps, loading]);

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const response = await aiService.conversationsList();
      const data = response.data || response;
      const list = Array.isArray(data) ? data : (data?.conversations || []);
      setConversations(list);
    } catch {
      // silently fail
    }
    setConversationsLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      loadConversations();
    }
  }, [open, loadConversations]);

  useEffect(() => {
    if (open) {
      aiService.getPromptTemplates().then(res => {
        if (res.success) {
          setPromptTemplates(res.data?.templates?.filter(t => Number(t.is_active) === 1) || []);
        }
      }).catch(() => {});
    }
  }, [open]);

  const { categories, categoryCounts, filteredTemplates } = useMemo(() => {
    const counts = { Todos: promptTemplates.length };
    promptTemplates.forEach(t => {
      const cat = t.category || 'Geral';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    const uniqueCats = ['Todos', ...Array.from(new Set(promptTemplates.map(t => t.category).filter(Boolean)))];
    const filtered = templateCategoryFilter === 'Todos'
      ? promptTemplates
      : promptTemplates.filter(t => (t.category || 'Geral') === templateCategoryFilter);
    return { categories: uniqueCats, categoryCounts: counts, filteredTemplates: filtered };
  }, [promptTemplates, templateCategoryFilter]);

  const filteredConversations = useMemo(() => {
    if (!conversationSearch.trim()) return conversations;
    const term = conversationSearch.toLowerCase();
    return conversations.filter(c => (c.title || '').toLowerCase().includes(term));
  }, [conversations, conversationSearch]);

  const selectConversation = async (convId) => {
    setActiveConversationId(convId);
    setMessages([]);
    setLoading(true);
    try {
      const response = await aiService.conversationsGet(convId);
      const data = response.data || response;
      const convMessages = data?.messages || [];
      setMessages(convMessages.map(m => {
        const tc = m.tool_calls || [];
        const atts = (m.attachments || []).map(a => ({
          ...a,
          file_url: a.file_url
            ? API_BASE + a.file_url.replace(/^\/api/, '')
            : null
        }));
        return {
          role: m.role,
          content: m.content,
          toolCalls: tc,
          toolsUsed: tc.map(t => t.function_description || t.function).filter(Boolean),
          timestamp: new Date(m.created_at).getTime() || Date.now(),
          attachments: atts
        };
      }));
    } catch {
      // silently fail
    }
    setLoading(false);
  };

  const newConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setSelectedTemplateId(null);
    setAttachments([]);
    setInput('');
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await aiService.conversationsDelete(convId);
      if (activeConversationId === convId) {
        setActiveConversationId(null);
        setMessages([]);
      }
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch {
      // silently fail
    }
  };

  const pollStartRef = useRef(null);

  const pollResult = useCallback(async (convId) => {
    const elapsed = Date.now() - (pollStartRef.current || Date.now());
    if (elapsed > 300000) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Tempo limite excedido. Tente novamente.', timestamp: Date.now(), error: true }]);
      setLoading(false);
      return;
    }
    try {
      const res = await aiService.checkAiResult(convId);
      const data = res.data || res;
      if (data?.status === 'completed') {
        setInterimStatus(null);
        setInterimToolCalls([]);
        setInterimSteps([]);
        const botContent = formatAiResponse(data);
        const toolsUsed = extractToolsUsed(data);
        const toolCalls = data?.tool_calls || [];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: botContent,
          toolsUsed,
          toolCalls,
          timestamp: Date.now()
        }]);
        setLoading(false);
        loadConversations();
        return;
      }
      if (data?.status === 'error') {
        setInterimStatus(null);
        setInterimToolCalls([]);
        setInterimSteps([]);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Erro ao processar sua solicitação.', timestamp: Date.now(), error: true }]);
        setLoading(false);
        return;
      }
      if (data?.status === 'processing' || data?.status === 'pending') {
        setInterimStatus(data?.status_message || null);
        if (data?.interim_steps) {
          setInterimSteps(data.interim_steps);
          const lastWithTools = [...data.interim_steps].reverse().find(s => s.tool_calls?.length > 0);
          if (lastWithTools) {
            setInterimToolCalls(lastWithTools.tool_calls);
          }
        } else if (data?.tool_calls && data.tool_calls.length > 0) {
          setInterimToolCalls(data.tool_calls);
        }
      }
      setTimeout(() => pollResult(convId), 2000);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erro ao processar sua solicitação.', timestamp: Date.now(), error: true }]);
      setLoading(false);
    }
  }, [loadConversations]);

  const sendMessage = async (customPrompt) => {
    const userMsg = customPrompt || input.trim();
    if (!userMsg || loading) return;
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    lastUserMsgRef.current = userMsg;

    const currentAttachments = [...attachments];
    const hasAttachments = currentAttachments.length > 0;

    const userMsgObj = { role: 'user', content: userMsg, timestamp: Date.now() };
    if (hasAttachments) {
      userMsgObj.attachments = currentAttachments.map(a => ({
        id: a.id,
        file_name: a.file_name,
        file_url: a.file_url,
        mime_type: a.mime_type
      }));
    }
    setMessages(prev => [...prev, userMsgObj]);
    setAttachments([]);
    setInterimStatus(null);
    setInterimToolCalls([]);
    setInterimSteps([]);
    setLoading(true);

    try {
      const payload = { prompt: userMsg };
      if (activeConversationId) {
        payload.conversation_id = activeConversationId;
      }
      if (selectedTemplateId && !activeConversationId) {
        payload.prompt_template_id = selectedTemplateId;
      }
      if (hasAttachments) {
        payload.attachment_ids = currentAttachments.map(a => a.id);
      }
      setSelectedTemplateId(null);
      const response = await aiService.executeAction(payload);
      const data = response.data || response;
      const newConvId = data?.conversation_id;
      if (newConvId && newConvId !== activeConversationId) {
        setActiveConversationId(newConvId);
        loadConversations();
      }
      if (data?.status === 'completed' && data?.response) {
        setInterimStatus(null);
        setInterimToolCalls([]);
        setInterimSteps([]);
        const botContent = formatAiResponse(data);
        const toolsUsed = extractToolsUsed(data);
        const toolCalls = data?.tool_calls || [];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: botContent,
          toolsUsed,
          toolCalls,
          timestamp: Date.now()
        }]);
        setLoading(false);
      } else if (data?.result && data?.function_used) {
        setInterimStatus(null);
        setInterimToolCalls([]);
        setInterimSteps([]);
        const toolCalls = data?.tool_calls || [{
          function: data.function_used,
          function_description: data.function_description,
          result: data.result,
          parameters: {}
        }];
        const botContent = formatAiResponse(data);
        const toolsUsed = [data.function_description || data.function_used];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: botContent,
          toolsUsed,
          toolCalls,
          timestamp: Date.now()
        }]);
        setLoading(false);
      } else {
        pollStartRef.current = Date.now();
        pollResult(newConvId);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erro ao processar sua solicitação.', timestamp: Date.now(), error: true }]);
      setLoading(false);
    }
  };

  const retryMessage = () => {
    if (lastUserMsgRef.current) {
      sendMessage(lastUserMsgRef.current);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setActiveConversationId(null);
    setAttachments([]);
  };

  const handleCopyMessage = (text, index) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMsgIndex(index);
      setTimeout(() => setCopiedMsgIndex(null), 2000);
    });
  };

  const handleFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    e.target.value = '';
    setUploading(true);
    try {
      for (const file of files) {
        const response = await aiService.uploadAttachment(file, activeConversationId);
        const data = response.data || response;
        if (data?.conversation_id && !activeConversationId) {
          setActiveConversationId(data.conversation_id);
          loadConversations();
        }
        const att = data?.attachment || data;
        if (att?.id) {
          const fileUrl = att.file_url
            ? API_BASE + att.file_url.replace(/^\/api/, '')
            : null;
          setAttachments(prev => [...prev, {
            id: att.id,
            file_name: att.file_name || file.name,
            file_url: fileUrl,
            mime_type: att.mime_type
          }]);
        }
      }
    } catch {
      // silently fail upload
    }
    setUploading(false);
  };

  const removeAttachment = async (attId) => {
    try {
      await aiService.deleteAttachment(attId);
    } catch {}
    setAttachments(prev => prev.filter(a => a.id !== attId));
  };

  const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.webp,.gif,.pdf,.xls,.xlsx,.csv,.txt';

  const deleteCurrentConversation = async () => {
    if (!activeConversationId) {
      clearChat();
      return;
    }
    try {
      await aiService.conversationsDelete(activeConversationId);
      setConversations(prev => prev.filter(c => c.id !== activeConversationId));
    } catch {}
    setMessages([]);
    setActiveConversationId(null);
  };

  const handleSidebarResizeStart = useCallback((e) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;
    setSidebarDragging(true);
  }, [sidebarWidth]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    setDragging(true);
  }, [panelWidth]);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      const diff = startXRef.current - e.clientX;
      const newWidth = Math.min(800, Math.max(320, startWidthRef.current + diff));
      if (onWidthChange) onWidthChange(newWidth);
    };
    const handleMouseUp = () => setDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, onWidthChange]);

  useEffect(() => {
    if (!sidebarDragging) return;
    const handleMouseMove = (e) => {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.min(340, Math.max(160, startWidthRef.current + diff));
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setSidebarDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarDragging]);

  return (
    <>
      <style>{`
        .ai-chat-spin { animation: aiChatSpin 0.8s linear infinite; display: inline-block; }
        @keyframes aiChatSpin { to { transform: rotate(360deg); } }

        .ai-pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          display: inline-block;
          box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          animation: aiPulse 2s infinite cubic-bezier(0.66, 0, 0, 1);
        }
        @keyframes aiPulse {
          to { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
        }

        .ai-typing-dot {
          width: 5px;
          height: 5px;
          background: #3b82f6;
          border-radius: 50%;
          display: inline-block;
          animation: aiTypingBounce 1.2s infinite ease-in-out both;
        }
        .ai-typing-dot:nth-child(1) { animation-delay: -0.32s; }
        .ai-typing-dot:nth-child(2) { animation-delay: -0.16s; }
        @keyframes aiTypingBounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        .ai-chat-sidebar {
          border-right: 1px solid var(--border);
          background: #f8fafc;
          overflow-y: auto;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }
        .ai-chat-sidebar-item {
          padding: 9px 12px;
          cursor: pointer;
          border-radius: 8px;
          margin: 2px 8px;
          font-size: 0.78rem;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          color: var(--text-main);
          border: 1px solid transparent;
        }
        .ai-chat-sidebar-item:hover {
          background: #ffffff;
          border-color: #e2e8f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .ai-chat-sidebar-item.active {
          background: #ffffff;
          border-left: 3px solid var(--primary);
          border-color: #e2e8f0 #e2e8f0 #e2e8f0 var(--primary);
          font-weight: 600;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
        }

        .ai-markdown {
          font-size: 0.8rem;
          line-height: 1.55;
          color: #1e293b;
        }
        .ai-markdown p { margin: 0 0 8px; }
        .ai-markdown p:last-child { margin-bottom: 0; }
        .ai-markdown strong { font-weight: 700; color: #0f172a; }
        .ai-markdown em { font-style: italic; }
        .ai-markdown ul, .ai-markdown ol { margin: 6px 0; padding-left: 20px; }
        .ai-markdown li { margin-bottom: 4px; }
        .ai-markdown hr { border: none; border-top: 1px solid var(--border); margin: 10px 0; }
        .ai-markdown h1, .ai-markdown h2, .ai-markdown h3, .ai-markdown h4 {
          margin: 10px 0 6px;
          font-weight: 700;
          line-height: 1.35;
          color: #0f172a;
        }
        .ai-markdown h1 { font-size: 1.05rem; }
        .ai-markdown h2 { font-size: 0.95rem; border-bottom: 1px solid #f1f5f9; padding-bottom: 4px; }
        .ai-markdown h3, .ai-markdown h4 { font-size: 0.88rem; }
        
        /* Modern markdown table styled identical to .table-modern */
        .ai-markdown table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin: 10px 0;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid var(--border);
          font-size: 0.74rem;
          background: #ffffff;
        }
        .ai-markdown thead th {
          background: #f8fafc;
          padding: 8px 12px;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-muted);
          border-bottom: 1px solid var(--border);
          text-align: left;
        }
        .ai-markdown tbody td {
          padding: 8px 12px;
          border-bottom: 1px solid var(--border);
          color: var(--text-main);
        }
        .ai-markdown tbody tr:last-child td {
          border-bottom: none;
        }
        .ai-markdown tbody tr:hover td {
          background: #f8fafc;
        }

        .ai-markdown code {
          background: #f1f5f9;
          color: #0f172a;
          padding: 2px 5px;
          border-radius: 4px;
          font-size: 0.73rem;
          font-family: Consolas, Menlo, Monaco, monospace;
        }
        .ai-markdown pre {
          background: #0f172a;
          color: #e2e8f0;
          padding: 10px 14px;
          border-radius: 8px;
          overflow-x: auto;
          font-size: 0.73rem;
          font-family: Consolas, Menlo, Monaco, monospace;
          margin: 8px 0;
          line-height: 1.5;
          border: 1px solid #1e293b;
        }
        .ai-markdown pre code {
          background: none;
          padding: 0;
          color: inherit;
          font-size: inherit;
        }
        .ai-markdown a {
          color: var(--accent);
          text-decoration: underline;
          font-weight: 500;
        }
        .ai-markdown blockquote {
          border-left: 3px solid var(--accent);
          margin: 8px 0;
          padding: 4px 12px;
          color: #475569;
          background: #f8fafc;
          border-radius: 0 6px 6px 0;
          font-style: italic;
        }

        .ai-chat-category-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 1px solid var(--border);
          background: var(--white);
          color: var(--text-main);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
          transition: all 0.15s ease;
          padding: 0;
        }
        .ai-chat-category-nav-btn:hover {
          background: #f1f5f9;
          color: var(--primary);
          border-color: var(--primary);
          transform: translateY(-50%) scale(1.08);
        }
        .ai-chat-category-nav-btn.left { left: -4px; }
        .ai-chat-category-nav-btn.right { right: -4px; }

        .ai-chat-input-container {
          background: #ffffff;
          border: 1px solid var(--border);
          border-radius: 14px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
          display: flex;
          flex-direction: column;
        }
        .ai-chat-input-container:focus-within {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.05);
        }

        .ai-btn-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 0;
        }
        .ai-btn-icon:hover {
          background: #f1f5f9;
          color: var(--text-main);
        }
        .ai-btn-icon.active {
          background: rgba(59, 130, 246, 0.12);
          color: var(--accent);
        }
      `}</style>

      {/* Main Drawer Panel */}
      <div
        className="ai-chat-panel"
        style={{
          display: 'flex',
          flexDirection: 'row',
          borderLeft: fullscreen ? 'none' : '1px solid var(--border)',
          background: 'var(--white)',
          position: 'fixed',
          top: fullscreen ? 0 : '70px',
          left: fullscreen ? 0 : 'auto',
          right: open ? 0 : `-${panelWidth + 10}px`,
          bottom: fullscreen ? 0 : 'auto',
          height: fullscreen ? '100vh' : 'calc(100vh - 70px)',
          zIndex: fullscreen ? 9999 : 1030,
          width: fullscreen ? '100vw' : `${panelWidth}px`,
          transition: dragging ? 'none' : 'right 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: open && !fullscreen ? '-8px 0 32px rgba(0, 0, 0, 0.06)' : 'none',
          borderRadius: fullscreen ? 0 : undefined
        }}
      >
        {/* Resize Handle */}
        {!fullscreen && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '6px',
              cursor: 'col-resize',
              zIndex: 20,
              background: dragging ? 'var(--primary)' : 'transparent',
              transition: 'background 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseDown={handleResizeStart}
            onMouseEnter={e => { if (!dragging) e.currentTarget.style.background = '#cbd5e1'; }}
            onMouseLeave={e => { if (!dragging) e.currentTarget.style.background = 'transparent'; }}
          />
        )}

        {/* Conversation Sidebar */}
        {open && showSidebar && (
          <div
            className="ai-chat-sidebar"
            style={{
              width: `${sidebarWidth}px`,
              position: 'relative'
            }}
          >
            {/* Sidebar Header */}
            <div style={{
              padding: '12px 14px 10px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiClock size={13} color="var(--text-muted)" />
                <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-main)' }}>Histórico</span>
              </div>
              <button
                onClick={newConversation}
                title="Nova conversa"
                style={{
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                <FiPlus size={12} /> Nova
              </button>
            </div>

            {/* Conversation Search */}
            <div style={{ padding: '8px 10px 4px', flexShrink: 0 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#ffffff',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '4px 8px'
              }}>
                <FiSearch size={12} color="#94a3b8" />
                <input
                  type="text"
                  value={conversationSearch}
                  onChange={e => setConversationSearch(e.target.value)}
                  placeholder="Buscar conversa..."
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '0.72rem',
                    width: '100%',
                    color: 'var(--text-main)'
                  }}
                />
                {conversationSearch && (
                  <button
                    onClick={() => setConversationSearch('')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8' }}
                  >
                    <FiX size={11} />
                  </button>
                )}
              </div>
            </div>

            {/* Conversation List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
              {conversationsLoading && (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                  <FiLoader className="ai-chat-spin" size={16} />
                </div>
              )}
              {!conversationsLoading && filteredConversations.length === 0 && (
                <div style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                  {conversationSearch ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa iniciada'}
                </div>
              )}
              {filteredConversations.map(conv => (
                <div
                  key={conv.id}
                  className={`ai-chat-sidebar-item ${activeConversationId === conv.id ? 'active' : ''}`}
                  onClick={() => selectConversation(conv.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
                    <FiMessageSquare size={13} style={{ opacity: activeConversationId === conv.id ? 1 : 0.6, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {conv.title || 'Sem título'}
                    </span>
                  </div>
                  <button
                    onClick={(e) => deleteConversation(conv.id, e)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      opacity: 0.5,
                      transition: 'all 0.15s ease',
                      color: 'var(--text-muted)',
                      borderRadius: 4
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                    title="Excluir conversa"
                  >
                    <FiTrash2 size={11} />
                  </button>
                </div>
              ))}
            </div>

            {/* Sidebar resize handle */}
            <div
              style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px',
                cursor: 'col-resize', zIndex: 10,
                background: sidebarDragging ? 'var(--primary)' : 'transparent',
                transition: 'background 0.2s'
              }}
              onMouseDown={handleSidebarResizeStart}
              onMouseEnter={e => { if (!sidebarDragging) e.currentTarget.style.background = '#cbd5e1'; }}
              onMouseLeave={e => { if (!sidebarDragging) e.currentTarget.style.background = 'transparent'; }}
            />
          </div>
        )}

        {/* Main Chat Area */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          {open && (
            <>
              {/* Header */}
              <div style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--white)',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    title={showSidebar ? 'Ocultar conversas' : 'Mostrar histórico'}
                    className={`ai-btn-icon ${showSidebar ? 'active' : ''}`}
                    style={{ width: '28px', height: '28px' }}
                  >
                    {showSidebar ? <FiChevronLeft size={16} /> : <FiLayers size={15} />}
                  </button>

                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '10px',
                      background: 'var(--primary)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(26, 26, 26, 0.15)'
                    }}>
                      <RiSparklingFill size={17} />
                    </div>
                    <span
                      className="ai-pulse-dot"
                      style={{
                        position: 'absolute',
                        bottom: -1,
                        right: -1,
                        border: '2px solid #ffffff'
                      }}
                      title="Agente online"
                    />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <h6 style={{
                      margin: 0,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      color: 'var(--text-main)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {activeConversationId ? (conversations.find(c => c.id === activeConversationId)?.title || 'Conversa') : 'Assistente IA'}
                    </h6>
                    <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>Winthor</span>
                      <span>•</span>
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>Pronto</span>
                    </div>
                  </div>
                </div>

                {/* Header Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  {messages.length > 0 && (
                    <button
                      onClick={deleteCurrentConversation}
                      title="Limpar conversa"
                      className="ai-btn-icon"
                      onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                  {!fullscreen ? (
                    <button
                      onClick={() => setFullscreen(true)}
                      title="Expandir tela cheia"
                      className="ai-btn-icon"
                    >
                      <FiMaximize2 size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => setFullscreen(false)}
                      title="Restaurar tamanho"
                      className="ai-btn-icon"
                    >
                      <FiMinimize2 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => { setFullscreen(false); onToggle(); }}
                    title="Fechar assistente"
                    className="ai-btn-icon"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>

              {/* Message Stream */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: 'var(--bg)'
              }}>
                {/* Empty State / Welcome Screen */}
                {messages.length === 0 && !loading && (
                  <div style={{
                    margin: 'auto 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '20px 10px',
                    textAlign: 'center'
                  }}>
                    {/* Hero Badge */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '14px',
                      background: 'var(--primary)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      boxShadow: '0 4px 14px rgba(26, 26, 26, 0.2)'
                    }}>
                      <RiSparklingFill size={24} />
                    </div>
                    <h5 style={{ fontSize: '0.95rem', margin: '0 0 4px', fontWeight: 700, color: 'var(--text-main)' }}>
                      Como posso ajudar você hoje?
                    </h5>
                    <p style={{ fontSize: '0.74rem', margin: '0 0 18px', color: 'var(--text-muted)', maxWidth: '380px', lineHeight: 1.4 }}>
                      Consulte relatórios, analise metas e acompanhe vendas em tempo real com o Agente de IA integrado.
                    </p>

                    {/* Quick Suggestion Cards */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '8px',
                      width: '100%',
                      maxWidth: '540px',
                      marginBottom: '20px'
                    }}>
                      {QUICK_SUGGESTIONS.map((sug, sIdx) => {
                        const Icon = sug.icon;
                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() => sendMessage(sug.prompt)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              textAlign: 'left',
                              padding: '10px 12px',
                              borderRadius: '10px',
                              background: '#ffffff',
                              border: '1px solid var(--border)',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = 'var(--primary)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = 'var(--border)';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.02)';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                              <Icon size={13} color="var(--primary)" />
                              <span style={{ fontWeight: 600, fontSize: '0.74rem', color: 'var(--text-main)' }}>
                                {sug.title}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                              {sug.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Prompt Templates Section */}
                    {!activeConversationId && promptTemplates.length > 0 && (
                      <div style={{ width: '100%', maxWidth: '540px', textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', padding: '0 2px' }}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Modelos de Prompt
                          </span>
                          <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>
                            {promptTemplates.length} disponíveis
                          </span>
                        </div>

                        {categories.length > 1 && (
                          <div style={{ marginBottom: 6 }}>
                            <CategoryScrollPills
                              categories={categories}
                              activeCategory={templateCategoryFilter}
                              onSelectCategory={setTemplateCategoryFilter}
                              categoryCounts={categoryCounts}
                            />
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '5px',
                          maxHeight: '180px',
                          overflowY: 'auto',
                          paddingRight: '2px'
                        }}>
                          {filteredTemplates.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => { setInput(t.prompt_text); setSelectedTemplateId(t.id); }}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid var(--border)',
                                background: '#ffffff',
                                cursor: 'pointer',
                                fontSize: '0.73rem',
                                color: 'var(--text-main)',
                                textAlign: 'left',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.borderColor = 'var(--primary)';
                                e.currentTarget.style.background = '#f8fafc';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.borderColor = 'var(--border)';
                                e.currentTarget.style.background = '#ffffff';
                              }}
                            >
                              <span style={{ fontWeight: 500, lineHeight: 1.3 }}>{t.title}</span>
                              {t.category && (
                                <span style={{
                                  fontSize: '0.62rem', background: '#f1f5f9', color: '#475569',
                                  padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap',
                                  fontWeight: 600, flexShrink: 0
                                }}>
                                  {t.category}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Messages Mapping */}
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: fullscreen ? '75%' : '92%',
                      width: msg.role === 'assistant' ? '100%' : 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3
                    }}
                  >
                    {/* User Message */}
                    {msg.role === 'user' ? (
                      <div style={{
                        background: 'var(--primary)',
                        color: '#ffffff',
                        borderRadius: '16px 16px 4px 16px',
                        padding: '9px 14px',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
                        alignSelf: 'flex-end',
                        maxWidth: '100%'
                      }}>
                        {msg.attachments?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {msg.attachments.map((att, atti) => {
                              const isImage = att.mime_type?.startsWith('image/');
                              return (
                                <div key={atti} style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                                  padding: 4, borderRadius: 8,
                                  background: 'rgba(255,255,255,0.15)',
                                  maxWidth: 180
                                }}>
                                  {isImage ? (
                                    <img
                                      src={att.file_url}
                                      alt={att.file_name}
                                      onClick={() => setZoomedImage(att.file_url)}
                                      style={{
                                        width: '100%', height: 100, objectFit: 'cover',
                                        borderRadius: 6, display: 'block',
                                        cursor: 'pointer', transition: 'transform 0.15s'
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '100%', height: 50,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)'
                                    }}>
                                      <FiFile size={20} />
                                    </div>
                                  )}
                                  <span style={{
                                    fontSize: '0.65rem', marginTop: 3, lineHeight: 1.2,
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap', maxWidth: '100%', color: 'rgba(255,255,255,0.9)'
                                  }}>
                                    {att.file_name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
                        </div>
                        {msg.timestamp && (
                          <div style={{ fontSize: '0.63rem', color: 'rgba(255, 255, 255, 0.65)', textAlign: 'right', marginTop: 4 }}>
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Assistant Message */
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}>
                        {/* Assistant Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          paddingLeft: 4
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              background: 'var(--primary)',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <RiSparklingFill size={12} />
                            </div>
                            <span style={{ fontWeight: 600, fontSize: '0.74rem', color: 'var(--text-main)' }}>
                              Assistente IA
                            </span>
                            {msg.timestamp && (
                              <span style={{ fontSize: '0.64rem', color: 'var(--text-muted)' }}>
                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>

                          {/* Action Toolbar on Message */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button
                              type="button"
                              onClick={() => handleCopyMessage(typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content), i)}
                              title="Copiar resposta"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '3px 6px',
                                borderRadius: '4px',
                                color: copiedMsgIndex === i ? '#16a34a' : 'var(--text-muted)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: '0.67rem',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              {copiedMsgIndex === i ? <FiCheck size={11} /> : <FiCopy size={11} />}
                              <span>{copiedMsgIndex === i ? 'Copiado!' : 'Copiar'}</span>
                            </button>
                          </div>
                        </div>

                        {/* Assistant Message Card */}
                        <div style={{
                          background: '#ffffff',
                          border: '1px solid var(--border)',
                          borderRadius: '16px 16px 16px 4px',
                          padding: '12px 16px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}>
                          {/* Collapsible Tool Calls Accordion */}
                          {Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0 && (
                            <ToolCallsAccordion toolCalls={msg.toolCalls} apiBase={API_BASE} />
                          )}

                          {/* Message Segments (Charts or Markdown) */}
                          {parseAiMessageSegments(msg.content).map((seg, sIdx) => {
                            if (seg.type === 'chart') {
                              return <AIChartCard key={sIdx} config={seg.config} />;
                            }
                            return (
                              <div
                                key={sIdx}
                                className="ai-markdown"
                                dangerouslySetInnerHTML={{
                                  __html: renderAiMarkdown(seg.content, API_BASE)
                                }}
                              />
                            );
                          })}

                          {/* Planilha Export Download Cards */}
                          {renderExportDownloadCards(msg.toolCalls, API_BASE)}

                          {/* Error Banner with Retry */}
                          {msg.error && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: 8,
                              padding: '8px 12px',
                              marginTop: 10
                            }}>
                              <span style={{ fontSize: '0.74rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiAlertCircle size={14} /> Houve uma falha no processamento.
                              </span>
                              <button
                                onClick={retryMessage}
                                title="Tentar novamente"
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #fca5a5',
                                  borderRadius: 6,
                                  padding: '4px 10px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  color: '#dc2626'
                                }}
                              >
                                <FiRefreshCw size={11} /> Tentar Novamente
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading / Thinking State */}
                {loading && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: fullscreen ? '75%' : '92%'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 4,
                      paddingLeft: 4
                    }}>
                      <div style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: 'var(--primary)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <RiSparklingFill size={12} />
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '0.74rem', color: 'var(--text-main)' }}>
                        Assistente IA
                      </span>
                    </div>

                    <div style={{
                      background: '#ffffff',
                      border: '1px solid var(--border)',
                      borderRadius: '16px 16px 16px 4px',
                      padding: '12px 16px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FiLoader className="ai-chat-spin" size={14} color="var(--primary)" />
                          <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-main)' }}>
                            {interimStatus || 'Processando solicitação...'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 3 }}>
                          <span className="ai-typing-dot" />
                          <span className="ai-typing-dot" />
                          <span className="ai-typing-dot" />
                        </div>
                      </div>

                      {/* Interim steps if available */}
                      {interimSteps.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                          {interimSteps.map((step, si) => (
                            <React.Fragment key={si}>
                              {step.content && step.content !== '' && (
                                <div style={{
                                  background: '#f8fafc',
                                  borderRadius: 6,
                                  padding: '6px 10px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '0.72rem',
                                  color: '#334155'
                                }}>
                                  {parseAiMessageSegments(step.content).map((seg, sIdx) => {
                                    if (seg.type === 'chart') {
                                      return <AIChartCard key={sIdx} config={seg.config} />;
                                    }
                                    return (
                                      <div
                                        key={sIdx}
                                        className="ai-markdown"
                                        dangerouslySetInnerHTML={{
                                          __html: renderAiMarkdown(
                                            seg.content.length > 500 ? seg.content.slice(0, 500) + '...' : seg.content,
                                            API_BASE
                                          )
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              {step.tool_calls && step.tool_calls.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1 }}>
                                  {step.tool_calls.map((tc, tci) => (
                                    <ToolCallCard key={tci} toolCall={tc} index={tci} apiBase={API_BASE} />
                                  ))}
                                  {renderExportDownloadCards(step.tool_calls, API_BASE)}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      {interimSteps.length === 0 && interimToolCalls.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                          {interimToolCalls.map((tc, tci) => (
                            <ToolCallCard key={tci} toolCall={tc} index={tci} apiBase={API_BASE} />
                          ))}
                          {renderExportDownloadCards(interimToolCalls, API_BASE)}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Bottom Input Area */}
              <div style={{
                padding: '10px 14px 14px',
                borderTop: '1px solid var(--border)',
                background: 'var(--white)',
                flexShrink: 0
              }}>
                {/* Popover de Modelos de Prompt */}
                {showTemplatePicker && (
                  <div style={{
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    padding: '12px',
                    marginBottom: '10px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                    maxHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiFileText size={14} color="var(--primary)" /> Modelos de Prompt ({promptTemplates.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowTemplatePicker(false)}
                        title="Fechar"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}
                      >
                        <FiX size={14} />
                      </button>
                    </div>

                    {categories.length > 1 && (
                      <CategoryScrollPills
                        categories={categories}
                        activeCategory={templateCategoryFilter}
                        onSelectCategory={setTemplateCategoryFilter}
                        categoryCounts={categoryCounts}
                      />
                    )}

                    <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px', paddingRight: '2px' }}>
                      {filteredTemplates.length === 0 ? (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', padding: '12px' }}>
                          Nenhum modelo nesta categoria.
                        </div>
                      ) : (
                        filteredTemplates.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              setInput(t.prompt_text);
                              setSelectedTemplateId(t.id);
                              setShowTemplatePicker(false);
                            }}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: '1px solid var(--border)',
                              background: '#f8fafc',
                              cursor: 'pointer',
                              fontSize: '0.73rem',
                              textAlign: 'left',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '8px',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = 'var(--primary)';
                              e.currentTarget.style.background = '#ffffff';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = 'var(--border)';
                              e.currentTarget.style.background = '#f8fafc';
                            }}
                          >
                            <span style={{ fontWeight: 500, lineHeight: 1.3 }}>{t.title}</span>
                            {t.category && (
                              <span style={{
                                fontSize: '0.62rem',
                                background: '#e2e8f0',
                                color: '#475569',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}>
                                {t.category}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Unified Modern Input Box */}
                <div className="ai-chat-input-container">
                  {/* Attachment chips inside top */}
                  {attachments.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '5px',
                      padding: '8px 12px 0 12px'
                    }}>
                      {attachments.map((att) => (
                        <div
                          key={att.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            fontSize: '0.7rem',
                            color: '#1d4ed8'
                          }}
                        >
                          <FiFile size={11} />
                          <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {att.file_name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#93c5fd', lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={e => e.currentTarget.style.color = '#93c5fd'}
                          >
                            <FiX size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Textarea */}
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={e => {
                      setInput(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Pergunte algo sobre vendas, metas ou relatórios..."
                    disabled={loading}
                    rows={1}
                    style={{
                      width: '100%',
                      padding: '10px 14px 4px',
                      border: 'none',
                      fontSize: '0.82rem',
                      outline: 'none',
                      background: 'transparent',
                      color: 'var(--text-main)',
                      resize: 'none',
                      minHeight: '40px',
                      maxHeight: '160px',
                      lineHeight: '1.45',
                      fontFamily: 'inherit'
                    }}
                  />

                  {/* Input Actions Bar (Bottom row inside card) */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 10px 8px 10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {promptTemplates.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                          disabled={loading}
                          title={showTemplatePicker ? 'Ocultar modelos' : 'Modelos de prompt'}
                          className={`ai-btn-icon ${showTemplatePicker ? 'active' : ''}`}
                          style={{ width: '28px', height: '28px' }}
                        >
                          <FiFileText size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleFilePicker}
                        disabled={loading || uploading}
                        title="Anexar arquivo ou planilha"
                        className="ai-btn-icon"
                        style={{ width: '28px', height: '28px' }}
                      >
                        {uploading ? <FiLoader className="ai-chat-spin" size={13} /> : <FiPaperclip size={14} />}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPTED_FILE_TYPES}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.64rem', color: '#94a3b8', userSelect: 'none' }}>
                        Enter ↵
                      </span>
                      <button
                        onClick={() => sendMessage()}
                        disabled={(!input.trim() && attachments.length === 0) || loading}
                        title="Enviar mensagem"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          border: 'none',
                          background: ((!input.trim() && attachments.length === 0) || loading) ? '#e2e8f0' : 'var(--primary)',
                          color: ((!input.trim() && attachments.length === 0) || loading) ? '#94a3b8' : '#ffffff',
                          cursor: ((!input.trim() && attachments.length === 0) || loading) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          boxShadow: ((!input.trim() && attachments.length === 0) || loading) ? 'none' : '0 2px 6px rgba(26, 26, 26, 0.2)'
                        }}
                      >
                        {loading ? <FiLoader className="ai-chat-spin" size={14} /> : <FiSend size={13} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Floating Launcher Button */}
      <button
        onClick={onToggle}
        title="Assistente IA"
        style={{
          position: 'fixed',
          bottom: '12px',
          right: '12px',
          zIndex: 1030,
          width: '38px',
          height: '38px',
          borderRadius: '10px',
          background: 'var(--primary)',
          color: 'white',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          cursor: 'pointer',
          boxShadow: '0 4px 14px -2px rgba(26, 26, 26, 0.35), 0 2px 4px -1px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          opacity: open ? 0 : 1,
          pointerEvents: open ? 'none' : 'auto',
          transform: open ? 'scale(0.8) translateY(10px)' : 'scale(1) translateY(0)'
        }}
        onMouseEnter={e => {
          if (!open) {
            e.currentTarget.style.transform = 'scale(1.08) translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(26, 26, 26, 0.42), 0 3px 6px -1px rgba(0, 0, 0, 0.12)';
          }
        }}
        onMouseLeave={e => {
          if (!open) {
            e.currentTarget.style.transform = 'scale(1) translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px -2px rgba(26, 26, 26, 0.35), 0 2px 4px -1px rgba(0, 0, 0, 0.08)';
          }
        }}
      >
        <RiSparklingFill size={18} />
        {/* Pulse online dot on trigger */}
        <span
          className="ai-pulse-dot"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 7,
            height: 7,
            border: '1.5px solid #1a1a1a'
          }}
        />
      </button>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 99999, background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            padding: 20
          }}
        >
          <img
            src={zoomedImage}
            alt="Zoom"
            style={{
              maxWidth: '90vw', maxHeight: '90vh',
              borderRadius: 10, objectFit: 'contain',
              boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6)'
            }}
          />
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}
          >
            <FiX size={28} color="white" />
          </div>
        </div>
      )}
    </>
  );
}

export default AIChatDrawer;
